import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Settings, Plus, Calendar, FileText, Pencil, Trash2, Shield } from 'lucide-react';

export const LeaveConfig = () => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    leave_type: '',
    days_per_year: 10,
    carry_forward_allowed: false,
    max_carry_forward: 0,
    requires_documents: false,
    min_notice_days: 1,
    approval_required: true,
  });

  const fetchPolicies = async () => {
    try {
      const response = await api.get('/leave/policies');
      setPolicies(response.data.data.policies);
    } catch (error) {
      toast.error('Failed to load leave policies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPolicies(); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/leave/policies/${editing}`, form);
        toast.success('Policy updated');
      } else {
        await api.post('/leave/policies', form);
        toast.success('Policy created');
      }
      setShowDialog(false);
      setEditing(null);
      fetchPolicies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save policy');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (policy) => {
    setForm({
      leave_type: policy.leave_type,
      days_per_year: policy.days_per_year,
      carry_forward_allowed: policy.carry_forward_allowed,
      max_carry_forward: policy.max_carry_forward,
      requires_documents: policy.requires_documents,
      min_notice_days: policy.min_notice_days,
      approval_required: policy.approval_required,
    });
    setEditing(policy.policy_id);
    setShowDialog(true);
  };

  const handleDelete = async (policyId) => {
    if (!window.confirm('Delete this leave policy?')) return;
    try {
      await api.delete(`/leave/policies/${policyId}`);
      toast.success('Policy deleted');
      fetchPolicies();
    } catch (error) {
      toast.error('Failed to delete policy');
    }
  };

  const openNew = () => {
    setForm({ leave_type: '', days_per_year: 10, carry_forward_allowed: false, max_carry_forward: 0, requires_documents: false, min_notice_days: 1, approval_required: true });
    setEditing(null);
    setShowDialog(true);
  };

  const typeLabels = {
    sick_leave: 'Sick Leave',
    casual_leave: 'Casual Leave',
    vacation: 'Vacation',
    maternity_leave: 'Maternity Leave',
    paternity_leave: 'Paternity Leave',
    unpaid_leave: 'Unpaid Leave',
    earned_leave: 'Earned Leave',
    compensatory: 'Compensatory Off',
  };

  const typeColors = {
    sick_leave: { bg: 'bg-rose-500/15', text: 'text-rose-400' },
    casual_leave: { bg: 'bg-blue-500/15', text: 'text-blue-400' },
    vacation: { bg: 'bg-amber-50', text: 'text-amber-600' },
    maternity_leave: { bg: 'bg-pink-500/15', text: 'text-pink-400' },
    paternity_leave: { bg: 'bg-indigo-500/15', text: 'text-indigo-400' },
    unpaid_leave: { bg: 'bg-slate-500/15', text: 'text-slate-400' },
    earned_leave: { bg: 'bg-emerald-50', text: 'text-emerald-600' },
    compensatory: { bg: 'bg-cyan-500/15', text: 'text-cyan-400' },
  };

  return (
    <div className="space-y-6" data-testid="leave-config-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-teal-50 flex items-center justify-center">
            <Settings className="h-5 w-5 text-teal-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Leave Policies</h1>
            <p className="text-sm text-slate-500">Configure leave types and rules for your school</p>
          </div>
        </div>
        <Button className="bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold rounded-xl" onClick={openNew} data-testid="add-policy-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Policy
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40"><div className="h-8 w-8 border-2 border-teal-500/30 border-t-teal-500 rounded-full animate-spin" /></div>
      ) : policies.length === 0 ? (
        <div className="glass-card p-12 text-center"><p className="text-slate-500">No leave policies configured yet.</p></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="policy-grid">
          {policies.map((policy) => {
            const tc = typeColors[policy.leave_type] || { bg: 'bg-white/10', text: 'text-slate-600' };
            return (
              <div key={policy.policy_id} className="glass-card p-5" data-testid={`policy-card-${policy.policy_id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold ${tc.bg} ${tc.text}`}>
                    <Calendar className="h-3 w-3 mr-1.5" />
                    {typeLabels[policy.leave_type] || policy.leave_type}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-teal-600" onClick={() => handleEdit(policy)} data-testid={`edit-policy-${policy.policy_id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-rose-400" onClick={() => handleDelete(policy.policy_id)} data-testid={`delete-policy-${policy.policy_id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>

                <div className="text-3xl font-bold text-white mb-1">{policy.days_per_year}</div>
                <p className="text-xs text-slate-400 mb-4">days per year</p>

                <div className="space-y-2.5 border-t border-slate-100 pt-3">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-500">Carry Forward</span>
                    <span className={policy.carry_forward_allowed ? 'text-emerald-600' : 'text-slate-300'}>
                      {policy.carry_forward_allowed ? `Up to ${policy.max_carry_forward} days` : 'No'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-500">Docs Required</span>
                    <span className={policy.requires_documents ? 'text-amber-600' : 'text-slate-300'}>
                      {policy.requires_documents ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-500">Notice Period</span>
                    <span className="text-slate-600">{policy.min_notice_days} day{policy.min_notice_days > 1 ? 's' : ''}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-500">Approval</span>
                    <span className={policy.approval_required ? 'text-teal-600' : 'text-slate-300'}>
                      {policy.approval_required ? 'Required' : 'Auto'}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Policy Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[#1a2332] border-slate-200 text-white max-w-md" data-testid="policy-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-800">{editing ? 'Edit' : 'Add'} Leave Policy</DialogTitle>
            <DialogDescription className="text-slate-500">Configure the rules for this leave type</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-slate-600 text-xs">Leave Type</Label>
              <Input
                value={form.leave_type}
                onChange={(e) => setForm({ ...form, leave_type: e.target.value })}
                placeholder="e.g. sick_leave, casual_leave"
                required
                disabled={!!editing}
                className="bg-white border-slate-200 text-white rounded-xl"
                data-testid="input-leave-type"
              />
              <p className="text-[10px] text-slate-400">Use snake_case: sick_leave, casual_leave, vacation, earned_leave</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-slate-600 text-xs">Days Per Year</Label>
                <Input type="number" min="1" max="365" value={form.days_per_year} onChange={(e) => setForm({ ...form, days_per_year: parseInt(e.target.value) || 0 })} className="bg-white border-slate-200 text-white rounded-xl" data-testid="input-days" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-600 text-xs">Min Notice Days</Label>
                <Input type="number" min="0" max="30" value={form.min_notice_days} onChange={(e) => setForm({ ...form, min_notice_days: parseInt(e.target.value) || 0 })} className="bg-white border-slate-200 text-white rounded-xl" data-testid="input-notice" />
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 rounded-xl bg-white border border-slate-200">
                <div>
                  <p className="text-sm text-slate-600">Carry Forward</p>
                  <p className="text-[10px] text-slate-400">Allow unused leaves to carry over</p>
                </div>
                <Switch checked={form.carry_forward_allowed} onCheckedChange={(v) => setForm({ ...form, carry_forward_allowed: v })} data-testid="switch-carry" />
              </div>
              {form.carry_forward_allowed && (
                <div className="space-y-2 pl-4">
                  <Label className="text-slate-600 text-xs">Max Carry Forward Days</Label>
                  <Input type="number" min="0" value={form.max_carry_forward} onChange={(e) => setForm({ ...form, max_carry_forward: parseInt(e.target.value) || 0 })} className="bg-white border-slate-200 text-white rounded-xl" data-testid="input-max-carry" />
                </div>
              )}
              <div className="flex items-center justify-between p-3 rounded-xl bg-white border border-slate-200">
                <div>
                  <p className="text-sm text-slate-600">Require Documents</p>
                  <p className="text-[10px] text-slate-400">E.g. medical certificate</p>
                </div>
                <Switch checked={form.requires_documents} onCheckedChange={(v) => setForm({ ...form, requires_documents: v })} data-testid="switch-docs" />
              </div>
              <div className="flex items-center justify-between p-3 rounded-xl bg-white border border-slate-200">
                <div>
                  <p className="text-sm text-slate-600">Approval Required</p>
                  <p className="text-[10px] text-slate-400">Needs admin/HR approval</p>
                </div>
                <Switch checked={form.approval_required} onCheckedChange={(v) => setForm({ ...form, approval_required: v })} data-testid="switch-approval" />
              </div>
            </div>
            <Button type="submit" className="w-full bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold rounded-xl" disabled={saving} data-testid="submit-policy">
              {saving ? 'Saving...' : editing ? 'Update Policy' : 'Create Policy'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeaveConfig;
